/* eslint-disable indent */
/* eslint-disable import/no-duplicates */
/* eslint-disable @typescript-eslint/camelcase */
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Select from '@material-ui/core/Select';
import { Subject } from '@material-ui/icons';
import SettingsIcon from '@material-ui/icons/Settings';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import EventIcon from '@material-ui/icons/Event';
import React from 'react';
import {
  ClientMaintenanceHistoryResponse,
  ClientMaintenanceHistoryRetrieveFields,
  EnvironmentMaintenanceHistoryResponse,
  EnvironmentMaintenanceHistoryRetrieveFields,
} from 'modules/maintenance/interfaces';
import Loading from 'components/Loading';
import { format, isValid, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import i18n from 'i18n';
import { formatDateWithMoment, removeHTMLTags } from 'utils/helpers';
import { Alert } from '@material-ui/lab';
import clsx from 'clsx';
import { useParams } from 'react-router';
import styles from './styles';

const useStyles = makeStyles(styles);

interface FormattedTableCellProps {
  value: any;
  oldValueToValidate?: string;
  classes: any;
  applyBorder?: boolean;
}

export interface Props {
  isLoadingMaintenance: boolean;
  maintenanceHistory:
    | EnvironmentMaintenanceHistoryResponse
    | ClientMaintenanceHistoryResponse;
  currentRequest: string;
  currentRequestDate: string;
  maintenanceHistoryRetrieve:
    | EnvironmentMaintenanceHistoryRetrieveFields
    | ClientMaintenanceHistoryRetrieveFields;
  context: 'client' | 'environment';
  retriever: (props) => void;
}

const MaintenanceHistory = ({
  isLoadingMaintenance,
  maintenanceHistory,
  maintenanceHistoryRetrieve,
  currentRequest,
  currentRequestDate,
  context,
  retriever,
}: Props) => {
  const { id } = useParams<{ id: string }>();
  const classes = useStyles();
  const [currentHistoryRequest, setCurrentHistoryRequest] = React.useState(
    currentRequest
  );

  const [
    currentHistoryRequestDate,
    setCurrentHistoryRequestDate,
  ] = React.useState(currentRequestDate);

  const [collapseSchedule, setCollapseSchedule] = React.useState<boolean>(true);
  const [collapseImmediate, setCollapseImmediate] = React.useState<boolean>(
    true
  );

  const handleChange = event => {
    setCurrentHistoryRequestDate(event?.target?.value[0]);
    setCurrentHistoryRequest(event?.target?.value[1]);
  };

  React.useEffect(() => {
    if (currentHistoryRequest) {
      retriever({
        id,
        request_uuid: currentHistoryRequest,
      });
    }
  }, [retriever, id, currentHistoryRequest]);

  const date = new Date(currentHistoryRequestDate);

  let formattedDate = '';

  if (String(date) !== 'Invalid Date') {
    formattedDate = format(date, i18n.t('MAINTENANCE_UPDATED_AT_KEY'), {
      locale: ptBR,
    });
  }

  const getValue = (object, path, defaultValue = '-') => {
    return path
      .split('.')
      .reduce(
        (value, key) =>
          value && value[key] !== undefined ? value[key] : defaultValue,
        object
      );
  };

  const createData = (field, old_value, new_value) => {
    return { field, old_value, new_value };
  };

  const createRow = (field, path, removeHtml = false) => {
    const previousValue = getValue(maintenanceHistoryRetrieve.previous, path);
    const currentValue = getValue(maintenanceHistoryRetrieve.current, path);

    return createData(
      field,
      removeHtml ? removeHTMLTags(previousValue) : previousValue,
      removeHtml ? removeHTMLTags(currentValue) : currentValue
    );
  };

  const rowsSchedule = [
    createRow(i18n.t('STATUS_KEY'), 'future_maintenance.enabled'),
    createRow(i18n.t('START_DATE_KEY'), 'future_maintenance.start_datetime'),
    createRow(i18n.t('COMPLETION_DATE_KEY'), 'future_maintenance.end_datetime'),
    ...(context !== 'client'
      ? [
          createRow(
            i18n.t('TERMINATE_INSTANCES_KEY'),
            'future_maintenance.terminate_instances'
          ),
        ]
      : []),
    createRow(
      i18n.t('TERMINATE_SESSIONS_KEY'),
      'future_maintenance.terminate_sessions'
    ),
    createRow(i18n.t('MESSAGE_KEY'), 'future_maintenance.message', true),
  ];

  const rowsImmediate = [
    createRow(i18n.t('STATUS_KEY'), 'current_maintenance.enabled'),
    createRow(i18n.t('MESSAGE_KEY'), 'current_maintenance.message', true),
    ...(context !== 'client'
      ? [
          createRow(
            i18n.t('TERMINATE_INSTANCES_KEY'),
            'current_maintenance.terminate_instances'
          ),
        ]
      : []),

    createRow(
      i18n.t('TERMINATE_SESSIONS_KEY'),
      'current_maintenance.terminate_sessions'
    ),
  ];

  const getFormattedValue = value => {
    if (typeof value === 'boolean') {
      return value ? i18n.t('ACTIVE_KEY') : i18n.t('INACTIVE_KEY');
    }

    const date = parseISO(value);
    if (isValid(date)) {
      return format(date, 'dd/MM/yyyy HH:mm');
    }

    return value;
  };

  const FormattedTableCell: React.FC<FormattedTableCellProps> = ({
    value,
    oldValueToValidate,
    classes,
    applyBorder,
  }) => {
    return (
      <TableCell
        className={clsx(classes.cell, {
          [classes.cellChanged]:
            oldValueToValidate !== undefined && value !== oldValueToValidate,
          [classes.cellBorder]: applyBorder,
        })}
        align={
          typeof value === 'boolean' ||
          isValid(parseISO(value)) ||
          value === '-'
            ? 'center'
            : 'left'
        }
      >
        {typeof value === 'boolean' ||
        isValid(parseISO(value)) ||
        value === '-' ? (
          getFormattedValue(value)
        ) : (
          <Grid item className={classes.textWithSubject}>
            <Subject className={classes.subject} />{' '}
            <Typography className={classes.textValue}>
              {getFormattedValue(value)}
            </Typography>
          </Grid>
        )}
      </TableCell>
    );
  };

  const getMaintenanceReason = reason => {
    if (reason === 'Updated by schedule maintenance') {
      return i18n.t('UPDATED_BY_SCHEDULE_MAINTENANCE_KEY');
    }
    if (reason === 'Modified by Customer Portal') {
      return i18n.t('MODIFIED_BY_CUSTOMER_PORTAL_KEY');
    }
    if (reason === 'Modified by API V2') {
      return i18n.t('MODIFIED_BY_API_V2_KEY');
    }
    if (reason === 'Modified by skyinone') {
      return i18n.t('MODIFIED_BY_SKYINONE_KEY');
    }
    return reason;
  };

  return isLoadingMaintenance ? (
    <Loading size={15} />
  ) : (
    <Grid container>
      {currentHistoryRequest !== undefined ? (
        <>
          <Grid container justify="space-between" alignItems="center">
            <Grid item xs={8}>
              <Typography className={classes.headerText}>
                {formattedDate}
              </Typography>
              {maintenanceHistoryRetrieve?.history_data?.changed_by?.email && (
                <Typography className={classes.headerText}>
                  {i18n.t('MAINTENANCE_CHANGED_BY_KEY', {
                    first_name:
                      maintenanceHistoryRetrieve?.history_data?.changed_by
                        ?.first_name,
                    last_name:
                      maintenanceHistoryRetrieve?.history_data?.changed_by
                        ?.last_name,
                    email:
                      maintenanceHistoryRetrieve?.history_data?.changed_by
                        ?.email,
                  })}
                </Typography>
              )}
              <Typography className={classes.headerText}>
                {i18n.t('MAINTENANCE_REASON_KEY', {
                  reason: getMaintenanceReason(
                    maintenanceHistoryRetrieve?.history_data?.reason
                  ),
                })}
              </Typography>
            </Grid>
            <Grid item xs={4} className={classes.selectorGrid}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel className={classes.inputLabel}>
                  {i18n.t('LOGS_PAGE.CHANGES_KEY')}
                </InputLabel>
                <Select
                  className={classes.select}
                  value={String(currentHistoryRequestDate)}
                  onChange={handleChange}
                  autoWidth
                  autoFocus
                  renderValue={value => `${formatDateWithMoment(value)}`}
                  rowsMax={10}
                >
                  {maintenanceHistory?.results?.map(result => (
                    <MenuItem
                      className={classes.menuItem}
                      key={result?.request_uuid}
                      value={[
                        `${result?.updated_at}`,
                        `${result?.request_uuid}`,
                      ]}
                    >
                      {result.changed_by?.email
                        ? `${result?.changed_by.first_name} ${
                            result?.changed_by.last_name
                          } ${i18n.t(
                            'AUDIT_SESSION.ON_KEY'
                          )} ${formatDateWithMoment(result?.updated_at)}`
                        : formatDateWithMoment(result?.updated_at)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container className={classes.container}>
            <Grid
              item
              xs={12}
              className={clsx(classes.maintenanceTitleGrid, {
                [classes.maintenanceTitleGridCollapsed]: !collapseSchedule,
              })}
              onClick={() => setCollapseSchedule(!collapseSchedule)}
            >
              <Grid container justify="center" alignItems="center">
                <EventIcon className={classes.titleIcon} />
                <Typography className={classes.maintenanceTitleValue}>
                  {i18n.t('MAINTENANCE_SCHEDULED_KEY')}
                </Typography>
              </Grid>

              <Grid
                container
                justify="flex-end"
                alignItems="center"
                className={classes.expandIcon}
              >
                {collapseSchedule ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Grid>
            </Grid>
            {collapseSchedule && (
              <TableContainer component={Paper}>
                <Table className={classes.table} size="small">
                  <TableHead>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.cell} align="left">
                        {i18n.t('FIELD_KEY')}
                      </TableCell>
                      <TableCell className={classes.cell} align="center">
                        {i18n.t('OLD_VALUE_KEY')}
                      </TableCell>
                      <TableCell className={classes.cell} align="center">
                        {i18n.t('NEW_VALUE_KEY')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rowsSchedule?.map(row => (
                      <TableRow key={row?.field} className={classes.tableRow}>
                        <TableCell
                          className={classes.cellTitle}
                          component="th"
                          scope="row"
                        >
                          {row?.field}
                        </TableCell>
                        <FormattedTableCell
                          value={row?.old_value}
                          classes={classes}
                          applyBorder
                        />
                        <FormattedTableCell
                          value={row?.new_value}
                          oldValueToValidate={row?.old_value}
                          classes={classes}
                        />
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
          <Grid container className={classes.container}>
            <Grid
              item
              xs={12}
              className={clsx(classes.maintenanceTitleGrid, {
                [classes.maintenanceTitleGridCollapsed]: !collapseImmediate,
              })}
              onClick={() => setCollapseImmediate(!collapseImmediate)}
            >
              <Grid container justify="center" alignItems="center">
                <SettingsIcon className={classes.titleIcon} />
                <Typography className={classes.maintenanceTitleValue}>
                  {i18n.t('MAINTENANCE_MANUAL_KEY')}
                </Typography>
              </Grid>

              <Grid
                container
                justify="flex-end"
                alignItems="center"
                className={classes.expandIcon}
              >
                {collapseImmediate ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Grid>
            </Grid>
            {collapseImmediate && (
              <TableContainer component={Paper}>
                <Table className={classes.table} size="small">
                  <TableHead>
                    <TableRow className={classes.tableRow}>
                      <TableCell className={classes.cell} align="left">
                        {i18n.t('FIELD_KEY')}
                      </TableCell>
                      <TableCell className={classes.cell} align="center">
                        {i18n.t('OLD_VALUE_KEY')}
                      </TableCell>
                      <TableCell className={classes.cell} align="center">
                        {i18n.t('NEW_VALUE_KEY')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rowsImmediate?.map(row => (
                      <TableRow key={row?.field} className={classes.tableRow}>
                        <TableCell
                          className={classes.cellTitle}
                          component="th"
                          scope="row"
                        >
                          {row?.field}
                        </TableCell>
                        <FormattedTableCell
                          value={row?.old_value}
                          classes={classes}
                          applyBorder
                        />
                        <FormattedTableCell
                          value={row?.new_value}
                          oldValueToValidate={row?.old_value}
                          classes={classes}
                        />
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </>
      ) : (
        <Alert className={classes.alert} severity="warning" color="info">
          <Grid item>
            <Typography>
              {i18n.t('NOT_ENOUGH_MAINTENANCE_HISTORY_DATA_KEY')}
            </Typography>
          </Grid>
        </Alert>
      )}
    </Grid>
  );
};

export default MaintenanceHistory;
